import React, { useMemo } from 'react';
import cx from 'classnames';

import CheckboxGroup from 'components/form/CheckboxGroup';
import RadioGroup from 'components/form/RadioGroup';
import { S } from 'components/typography';

import { VisitorCustomFieldType } from 'graphql-query-contracts';

import type { CustomFieldOptionItem, CustomFieldRendererProps } from './customField.types';

import styles from './CustomFieldRenderer.scss';

function getOptionDisplay(option: CustomFieldOptionItem): string {
  return typeof option === 'string' ? option : option.displayText;
}

function getOptionValue(option: CustomFieldOptionItem): string {
  return typeof option === 'string' ? option : option.value;
}

const OPTION_SCHEMA = { id: 'value' as const, text: 'displayText' as const };
const EMPTY_CUSTOM_FIELD_OPTIONS: CustomFieldOptionItem[] = [];

export function CustomFieldRenderer({
  field,
  disabled = true,
  value,
  onChange,
}: CustomFieldRendererProps) {
  const optionList = field.options ?? EMPTY_CUSTOM_FIELD_OPTIONS;
  const isInteractive = !disabled && !!onChange;
  const stringValue = typeof value === 'string' ? value : '';
  const arrayValue = Array.isArray(value) ? value : [];

  const items = useMemo(
    () =>
      optionList.map(opt => ({
        value: getOptionValue(opt),
        displayText: getOptionDisplay(opt),
      })),
    [optionList]
  );

  function renderSelectField() {
    return (
      <select
        className={styles.fieldSelect}
        disabled={disabled}
        value={stringValue}
        onChange={isInteractive ? event => onChange(event.target.value) : () => undefined}
        data-test={`customField-select-${field.fieldKey}`}
      >
        <option value="">{field.placeholder ?? ''}</option>
        {optionList.map(option => (
          <option key={getOptionValue(option)} value={getOptionValue(option)}>
            {getOptionDisplay(option)}
          </option>
        ))}
      </select>
    );
  }

  function renderRadioField() {
    return (
      <fieldset
        className={cx(styles.options, styles.optionsFieldset, styles.optionsRadio)}
        data-test={`customField-radio-${field.fieldKey}`}
      >
        <RadioGroup
          name={field.fieldKey}
          disabled={disabled}
          selected={isInteractive ? stringValue || null : null}
          schema={OPTION_SCHEMA}
          items={items}
          className={styles.optionControl}
          onChange={isInteractive ? selected => onChange(selected) : () => undefined}
          doTranslate={false}
        />
      </fieldset>
    );
  }

  function renderCheckboxField() {
    return (
      <fieldset
        className={cx(styles.options, styles.optionsFieldset, styles.optionsCheckbox)}
        data-test={`customField-checkbox-${field.fieldKey}`}
      >
        <CheckboxGroup
          name={field.fieldKey}
          disabled={disabled}
          selected={isInteractive ? arrayValue : []}
          schema={OPTION_SCHEMA}
          items={items}
          onChange={
            isInteractive ? (values: string[]) => onChange(values) : () => undefined
          }
        />
      </fieldset>
    );
  }

  function renderTextareaField() {
    return (
      <textarea
        className={styles.fieldTextarea}
        disabled={disabled}
        placeholder={field.placeholder ?? ''}
        value={stringValue}
        readOnly={!isInteractive}
        onChange={isInteractive ? event => onChange(event.target.value) : undefined}
        data-test={`customField-textarea-${field.fieldKey}`}
      />
    );
  }

  function renderTextField() {
    return (
      <input
        type="text"
        className={styles.fieldInput}
        disabled={disabled}
        placeholder={field.placeholder ?? ''}
        value={stringValue}
        readOnly={!isInteractive}
        onChange={isInteractive ? event => onChange(event.target.value) : undefined}
        data-test={`customField-text-${field.fieldKey}`}
      />
    );
  }

  function renderInput() {
    switch (field.fieldType) {
      case VisitorCustomFieldType.Textarea:
        return renderTextareaField();
      case VisitorCustomFieldType.SelectDd:
        return renderSelectField();
      case VisitorCustomFieldType.SelectRadio:
        return renderRadioField();
      case VisitorCustomFieldType.MultiSelect:
        return renderCheckboxField();
      default:
        return renderTextField();
    }
  }

  return (
    <div
      className={cx(styles.fieldWrapper, { [styles.interactive]: isInteractive })}
      data-test={`customFieldRenderer-${field.fieldKey}`}
    >
      <S as="label" block>
        {field.fieldName}
      </S>
      {renderInput()}
    </div>
  );
}

